/**
 * Values typed as `{{x}}` in a step (G-camp-cs-14): the hint says "type {{", but a value is
 * written `{{ state.x }}`. A name the agent has is completed to that form as it is typed; any
 * other `{{…}}` that is not a value is flagged by the checks in plain words (the save would
 * otherwise fail with a technical message, and the data checks silently stop).
 */

/** A `{{ … }}` reference and what is inside it. */
const REF = /\{\{\s*([^{}]*?)\s*\}\}/g;
/** A plain name (and fields or items inside it), not yet written as `state.…`. */
const BARE = /^([A-Za-z_]\w*)((?:\.[A-Za-z_]\w*|\[\d+\])*)$/;
/** A value of the run: `state.x`, `state.x.y`, `state.x[0]`. */
const STATE = /^state\.[A-Za-z_]\w*(?:\.[A-Za-z_]\w*|\[\d+\])*$/;

const rootOf = (path: string) => path.split(/[.[]/)[0] as string;

/** `{{x}}` / `{{x.y}}` → `{{ state.x }}` / `{{ state.x.y }}` when `x` is a value the agent has. */
export function fixTemplate(text: string, known: readonly string[]): string {
  if (!text.includes("{{")) return text;
  const roots = new Set(known.map(rootOf));
  return text.replace(REF, (whole, inner: string) => {
    const m = BARE.exec(inner);
    if (!m || m[1] === "state" || !roots.has(m[1] as string)) return whole;
    return `{{ state.${inner} }}`;
  });
}

/** The same, through a step's values (arguments may be groups and lists). */
export function fixTemplates<T>(value: T, known: readonly string[]): T {
  if (typeof value === "string") return fixTemplate(value, known) as T;
  if (Array.isArray(value)) return value.map((v) => fixTemplates(v, known)) as T;
  if (value && typeof value === "object" && !("$literal" in (value as object)) && !("$file" in (value as object))) {
    return Object.fromEntries(Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, fixTemplates(v, known)])) as T;
  }
  return value;
}

export interface BadTemplate {
  /** As written, e.g. `{{category}}`. */
  ref: string;
  /** `state.category` when the name is a value the agent has. */
  suggestion: string | null;
}

/**
 * References in a step's values that are not a value of the run (`{{category}}`, `{{ x y }}`):
 * the platform can't read them. Expressions (`{{ state.a + 1 }}`) are left to the server.
 */
export function badTemplates(value: unknown, known: readonly string[]): BadTemplate[] {
  const out: BadTemplate[] = [];
  const roots = new Set(known.map(rootOf));
  const walk = (v: unknown) => {
    if (typeof v === "string") {
      for (const m of v.matchAll(REF)) {
        const inner = (m[1] ?? "").trim();
        if (STATE.test(inner) || /\bstate\./.test(inner)) continue;
        const bare = BARE.exec(inner);
        const suggestion = bare && bare[1] !== "state" && roots.has(bare[1] as string) ? `state.${inner}` : null;
        if (!out.some((x) => x.ref === m[0])) out.push({ ref: m[0], suggestion });
      }
    } else if (Array.isArray(v)) v.forEach(walk);
    else if (v && typeof v === "object" && !("$literal" in (v as object)) && !("$file" in (v as object))) Object.values(v as object).forEach(walk);
  };
  walk(value);
  return out;
}
